import { useCallback, useEffect, useRef } from 'react';
import { normalizeCardName } from 'src/util';
// import { compressCardData, insertUrlParam } from 'src/util'; // Unused - URL parameter feature disabled
import { useCard } from './use-card';
import { useSetting } from './use-setting';
import { notification } from 'antd';
import { useLanguage } from './use-i18n';
import { useBatchDownload } from './use-batch-download';
import { ExportCallbackParameter } from './use-master-seri';

export type UseCardExport = {
    isTainted: boolean,
    isInitializing: boolean,
    exportCanvasRef: React.RefObject<HTMLCanvasElement>,
    exportRef: React.MutableRefObject<{
        currentPipeline: Promise<void>;
        pipelineRunning: boolean;
    }>,
    onExport: (
        exportProps: Pick<ExportCallbackParameter, 'isPendulum' | 'isRelevant' | 'opacity' | 'pendulumSize'>,
    ) => Promise<void>,
    onDownloadError: () => void,
    onDownloadComplete: () => void,
};
/** To ensure the best performance, all of this hook's paramter must be memozied. */
export const useCardExport = ({
    isTainted,
    isInitializing,
    exportCanvasRef,
    exportRef,
    onExport,
    onDownloadError,
    onDownloadComplete,
}: UseCardExport) => {
    const language = useLanguage();
    const {
        card: currentCard,
    } = useCard();
    const {
        addToBatch,
        batchDataMap,
        isBatchDownloading,
    } = useBatchDownload();
    const resolution = useSetting(state => state.setting.resolution);
    const exportScheme = useSetting(state => state.setting.exportScheme);
    const {
        opacity,
        name,
        isPendulum,
        pendulumSize,
    } = currentCard;
    const normalizedName = normalizeCardName(name);
    const pendingSave = useRef(false);

    const getCardDataUrl = useCallback(async (size: [number, number] = resolution) => {
        const drawCanvas = exportCanvasRef.current;
        if (!drawCanvas) return undefined;

        const drawCanvasContext = drawCanvas.getContext('2d');
        if (!drawCanvasContext) return undefined;

        try {
            /** Clone node so we can resize it as will */
            const clonedCanvas = document.createElement('canvas');
            const clonedCanvasContext = clonedCanvas.getContext('2d');

            if (!clonedCanvasContext) return undefined;

            // Calculate bleed: 3mm at 300 DPI = ~35.43 pixels
            // Scale bleed proportionally to the output size
            const baseWidth = 813; // CanvasConst.width
            const targetWidth = size[0];
            const targetHeight = size[1];
            const scaleRatio = targetWidth / baseWidth;
            const bleedSize = Math.round(36 * scaleRatio); // 36 pixels bleed at base size (3mm at 300 DPI)

            // Add bleed to all sides
            clonedCanvas.width = targetWidth + (bleedSize * 2);
            clonedCanvas.height = targetHeight + (bleedSize * 2);

            // Fill base with black as fallback
            clonedCanvasContext.fillStyle = '#000000';
            clonedCanvasContext.fillRect(0, 0, clonedCanvas.width, clonedCanvas.height);

            clonedCanvasContext.imageSmoothingEnabled = true;
            clonedCanvasContext.imageSmoothingQuality = 'high';

            // Draw card at normal size
            clonedCanvasContext.drawImage(
                drawCanvas,
                0, 0, drawCanvas.width, drawCanvas.height,
                bleedSize, bleedSize, targetWidth, targetHeight,
            );

            // Apply subtle blur only to bleed areas
            const blurAmount = Math.max(2, Math.round(3 * scaleRatio));
            clonedCanvasContext.filter = `blur(${blurAmount}px)`;

            // Stretch border edges into bleed WITH OVERLAP to hide seam
            const borderSample = Math.max(5, Math.round(8 * scaleRatio));
            const overlap = Math.max(3, Math.round(5 * scaleRatio)); // Overlap amount

            // TOP: Stretch upward AND overlap down into card
            clonedCanvasContext.drawImage(
                clonedCanvas,
                bleedSize, bleedSize, targetWidth, borderSample,
                bleedSize, 0, targetWidth, bleedSize + overlap,
            );
            // BOTTOM: Stretch downward AND overlap up into card
            clonedCanvasContext.drawImage(
                clonedCanvas,
                bleedSize, bleedSize + targetHeight - borderSample, targetWidth, borderSample,
                bleedSize, bleedSize + targetHeight - overlap, targetWidth, bleedSize + overlap,
            );
            // LEFT: Stretch leftward AND overlap right into card
            clonedCanvasContext.drawImage(
                clonedCanvas,
                bleedSize, bleedSize, borderSample, targetHeight,
                0, bleedSize, bleedSize + overlap, targetHeight,
            );
            // RIGHT: Stretch rightward AND overlap left into card
            clonedCanvasContext.drawImage(
                clonedCanvas,
                bleedSize + targetWidth - borderSample, bleedSize, borderSample, targetHeight,
                bleedSize + targetWidth - overlap, bleedSize, bleedSize + overlap, targetHeight,
            );
            // Corners with overlap
            clonedCanvasContext.drawImage(
                clonedCanvas,
                bleedSize, bleedSize, borderSample, borderSample,
                0, 0, bleedSize + overlap, bleedSize + overlap,
            );
            clonedCanvasContext.drawImage(
                clonedCanvas,
                bleedSize + targetWidth - borderSample, bleedSize, borderSample, borderSample,
                bleedSize + targetWidth - overlap, 0, bleedSize + overlap, bleedSize + overlap,
            );
            clonedCanvasContext.drawImage(
                clonedCanvas,
                bleedSize, bleedSize + targetHeight - borderSample, borderSample, borderSample,
                0, bleedSize + targetHeight - overlap, bleedSize + overlap, bleedSize + overlap,
            );
            clonedCanvasContext.drawImage(
                clonedCanvas,
                bleedSize + targetWidth - borderSample, bleedSize + targetHeight - borderSample, borderSample, borderSample,
                bleedSize + targetWidth - overlap, bleedSize + targetHeight - overlap, bleedSize + overlap, bleedSize + overlap,
            );

            clonedCanvasContext.filter = 'none';

            // Try toDataURL (will throw if canvas is tainted)
            return clonedCanvas.toDataURL('image/png');
        } catch (e) {
            // If tainted, try using toBlob with a workaround
            console.error('[EXPORT] Canvas is tainted, trying blob workaround:', e);

            try {
                // Create a new canvas for blob export
                const blobCanvas = document.createElement('canvas');
                const blobContext = blobCanvas.getContext('2d');

                if (!blobContext) return undefined;

                const baseWidth = 813;
                const targetWidth = size[0];
                const targetHeight = size[1];
                const scaleRatio = targetWidth / baseWidth;
                const bleedSize = Math.round(36 * scaleRatio);

                blobCanvas.width = targetWidth + (bleedSize * 2);
                blobCanvas.height = targetHeight + (bleedSize * 2);

                blobContext.fillStyle = '#000000';
                blobContext.fillRect(0, 0, blobCanvas.width, blobCanvas.height);

                blobContext.imageSmoothingEnabled = true;
                blobContext.imageSmoothingQuality = 'high';

                blobContext.drawImage(
                    drawCanvas,
                    0, 0, drawCanvas.width, drawCanvas.height,
                    bleedSize, bleedSize, targetWidth, targetHeight,
                );

                const blurAmount = Math.max(2, Math.round(3 * scaleRatio));
                blobContext.filter = `blur(${blurAmount}px)`;

                const borderSample = Math.max(5, Math.round(8 * scaleRatio));
                const overlap = Math.max(3, Math.round(5 * scaleRatio));

                blobContext.drawImage(
                    blobCanvas,
                    bleedSize, bleedSize, targetWidth, borderSample,
                    bleedSize, 0, targetWidth, bleedSize + overlap,
                );
                blobContext.drawImage(
                    blobCanvas,
                    bleedSize, bleedSize + targetHeight - borderSample, targetWidth, borderSample,
                    bleedSize, bleedSize + targetHeight - overlap, targetWidth, bleedSize + overlap,
                );
                blobContext.drawImage(
                    blobCanvas,
                    bleedSize, bleedSize, borderSample, targetHeight,
                    0, bleedSize, bleedSize + overlap, targetHeight,
                );
                blobContext.drawImage(
                    blobCanvas,
                    bleedSize + targetWidth - borderSample, bleedSize, borderSample, targetHeight,
                    bleedSize + targetWidth - overlap, bleedSize, bleedSize + overlap, targetHeight,
                );
                blobContext.drawImage(
                    blobCanvas,
                    bleedSize, bleedSize, borderSample, borderSample,
                    0, 0, bleedSize + overlap, bleedSize + overlap,
                );
                blobContext.drawImage(
                    blobCanvas,
                    bleedSize + targetWidth - borderSample, bleedSize, borderSample, borderSample,
                    bleedSize + targetWidth - overlap, 0, bleedSize + overlap, bleedSize + overlap,
                );
                blobContext.drawImage(
                    blobCanvas,
                    bleedSize, bleedSize + targetHeight - borderSample, borderSample, borderSample,
                    0, bleedSize + targetHeight - overlap, bleedSize + overlap, bleedSize + overlap,
                );
                blobContext.drawImage(
                    blobCanvas,
                    bleedSize + targetWidth - borderSample, bleedSize + targetHeight - borderSample, borderSample, borderSample,
                    bleedSize + targetWidth - overlap, bleedSize + targetHeight - overlap, bleedSize + overlap, bleedSize + overlap,
                );

                blobContext.filter = 'none';

                // Convert to blob then to data URL
                const blob = await new Promise<Blob | null>((resolve) => {
                    blobCanvas.toBlob(resolve, 'image/png');
                });

                if (!blob) throw new Error('Failed to create blob');

                // Convert blob to data URL
                return await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            } catch (blobError) {
                console.error('[EXPORT] Blob method also failed:', blobError);
                return undefined;
            }
        }
    }, [exportCanvasRef, resolution]);
    const addToCurrentBatch = useCallback(async (
        cardName: string,
        cardId: string,
        size: [number, number] = resolution,
    ) => {
        try {
            const cardData = getCardDataUrl(size);
            if (typeof cardData !== 'string') throw new Error('Failed to add card to batch');
            const fetchedDataUrl = await fetch(cardData);
            const blob = await fetchedDataUrl.blob();
            if (isBatchDownloading) addToBatch(cardId, cardName, blob);
        } catch (e) {
            onDownloadError();
        }
        document.querySelector('#export-canvas-guard')?.classList.remove('guard-on');
        onDownloadComplete();
    }, [addToBatch, getCardDataUrl, isBatchDownloading, onDownloadComplete, onDownloadError, resolution]);
    const download = useCallback(async (size: [number, number] = resolution) => {
        try {
            const normalizedName = normalizeCardName(name);
            const cardData = await getCardDataUrl(size);

            if (typeof cardData !== 'string') {
                console.error('Failed to generate card data URL - likely tainted canvas');
                throw new Error('Failed to trigger automatic download');
            }

            console.log('Card data URL generated successfully with bleed');

            var link = document.createElement('a');
            link.download = normalizedName
                ? `${normalizedName}.png`
                : 'card.png';
            link.href = cardData;
            link.click();

            console.log('Download triggered successfully for:', link.download);
        } catch (e) {
            console.error('Download error:', e);
            onDownloadError();
        }
        document.querySelector('#export-canvas-guard')?.classList.remove('guard-on');
        onDownloadComplete();
    }, [resolution, onDownloadComplete, name, getCardDataUrl, onDownloadError]);
    const onSave = (size?: [number, number]) => {
        document.querySelector('#export-canvas-guard')?.classList.add('guard-on');
        const queuingSize = size ? [...size] as [number, number] : undefined;
        /** Prevent split-second download. By using hotkey user is able to perform extremely fast save process before the pipeline start running, thus getting the previous card data instead of the most recent one. */
        setTimeout(() => {
            if (exportRef.current.pipelineRunning === false) {
                download(queuingSize);
            } else pendingSave.current = true;
        }, 200);
    };

    useEffect(() => {
        let relevant = true;
        setTimeout(() => {
            if (relevant) {
                document.title = normalizedName
                    ? `${normalizedName} - Yu-Gi-Oh Carder`
                    : 'Yu-Gi-Oh Carder';
            }
        }, 500);

        return () => {
            relevant = false;
        };
    }, [normalizedName]);

    useEffect(() => {
        let saveBeforeReload = () => {
            localStorage.setItem('card-data', JSON.stringify(currentCard));
            localStorage.setItem('card-version', process.env.REACT_APP_VERSION ?? 'unknown');
        };
        window.addEventListener('beforeunload', saveBeforeReload);

        return () => {
            window.removeEventListener('beforeunload', saveBeforeReload);
        };
    });

    useEffect(() => {
        let relevant = true;
        let confirmReload = (ev: Event) => {
            ev.preventDefault();
            return language['prompt.warning.on-leave.label'];
        };
        if (isInitializing === false) {
            try {
                localStorage.setItem('card-data', JSON.stringify(currentCard));
                localStorage.setItem('card-version', process.env.REACT_APP_VERSION ?? 'unknown');
            } catch (e) {
                try {
                    const { artData, backgroundData, ...shortenedCard } = currentCard;
                    localStorage.setItem('card-data', JSON.stringify(shortenedCard));
                    localStorage.setItem('card-version', process.env.REACT_APP_VERSION ?? 'unknown');
                } catch (e) {
                    /** Ensure it does not fire repeatedly */
                    const key = 'fail-to-set-storage-notification';
                    notification.close(key);
                    notification.info({
                        key,
                        message: language['error.card-max-size.message'],
                        description: language['error.card-max-size.description'],
                    });
                }
            }

            /**
             * Run export pipeline:
             * - Immediately mark the pipeline as running, then run wait for the current pipeline (1).
             * - If the (1) pipeline is no longer relevant, end effect.
             * - Otherwise (the current pipeline is the newest), start export pipeline, this export pipeline is now the current pipeline (2).
             * - If the (2) pipeline is no longer relevant, end effect.
             * - Otherwise finish the pipeline and write result, also mark the pipeline as no longer running.
             */
            (async () => {
                const canvasRef = exportCanvasRef.current;
                if (canvasRef) {
                    window.addEventListener('beforeunload', confirmReload);
                    document.getElementById('export-canvas')?.classList.remove('js-export-available');
                    document.getElementById('preview-canvas')?.classList.remove('js-export-available');
                    document.getElementById('export-canvas-guard')?.setAttribute('style', '');
                    document.getElementById('save-button-waiting')?.setAttribute('style', 'display: block');

                    exportRef.current.pipelineRunning = true;
                    /** Artifical delay, turn on to test export timing */
                    // await new Promise(resolve => setTimeout(() => resolve(true), 3000));
                    await exportRef.current.currentPipeline;

                    if (relevant) {
                        exportRef.current.currentPipeline = onExport({
                            isPendulum,
                            opacity,
                            pendulumSize,
                            isRelevant: () => relevant,
                        });

                        await exportRef.current.currentPipeline;
                        if (relevant) {
                            /** Never include art data here, it will easily exceed the limit of url length */
                            // URL parameter disabled - no longer updating URL with card data
                            const normalizedCard = { ...currentCard, artData: '', backgroundData: '' };
                            // const condensedCard = JSON.stringify(compressCardData(normalizedCard));
                            // if (typeof condensedCard === 'string') insertUrlParam('data', condensedCard);

                            document.getElementById('export-canvas')?.classList.add('js-export-available');
                            document.getElementById('preview-canvas')?.classList.add('js-export-available');
                            document.getElementById('export-canvas-guard')?.setAttribute('style', 'display: none');
                            document.getElementById('save-button-waiting')?.setAttribute('style', 'display: none');
                            window.removeEventListener('beforeunload', confirmReload);
                            exportRef.current.pipelineRunning = false;

                            if (pendingSave.current) {
                                pendingSave.current = false;
                                download();
                            }
                            if (isBatchDownloading && !batchDataMap[normalizedCard.id]) {
                                setTimeout(() => {
                                    addToCurrentBatch(
                                        exportScheme === 'with-name'
                                            ? `${normalizedName}.png`
                                            : `${normalizedCard.setId} - ${normalizedName}.png`,
                                        normalizedCard.id,
                                    );
                                }, 200);
                            }
                        }
                    }
                }
            })();
        }

        return () => {
            window.removeEventListener('beforeunload', confirmReload);
            relevant = false;
        };
    });

    return {
        onSave,
        isPipelineRunning: () => exportRef.current.pipelineRunning,
    };
};